import { validateColumnDrafts, type ColumnDraft } from './columnDrafts';

/**
 * Step decisions for the create-a-data-mapping wizard, as pure functions.
 *
 * The wizard's `validate` callbacks own the side effects (uploading, setting
 * field errors); the branching that decides *what* should happen lives here so
 * it can be tested directly.
 */

export type SourceStepDecision =
  'ok' | 'upload' | 'need-file' | 'need-existing-asset';

export function sourceStepDecision(state: {
  sourceMode: 'upload' | 'existing' | 'none';
  hasSource: boolean;
  hasFile: boolean;
  existingAssetIsUsable: boolean;
}): SourceStepDecision {
  if (state.sourceMode === 'none') return 'ok';
  if (state.sourceMode === 'existing') {
    return state.existingAssetIsUsable ? 'ok' : 'need-existing-asset';
  }
  // Already uploaded and no new pick — the user stepped back and forward again.
  if (state.hasSource && !state.hasFile) return 'ok';
  return state.hasFile ? 'upload' : 'need-file';
}

export type SchemaStepDecision =
  | { ok: true }
  | { ok: false; errors: Record<string, string>; message?: string };

export function schemaStepDecision(state: {
  tableMode: 'new' | 'existing';
  columns: ColumnDraft[];
  keyColumnIds: string[];
}): SchemaStepDecision {
  // An existing table brings its own columns, so there is nothing to check.
  if (state.tableMode === 'existing') return { ok: true };

  const validation = validateColumnDrafts(state.columns, state.keyColumnIds);
  if (validation.ok) return { ok: true };
  return { ok: false, errors: validation.errors, message: validation.message };
}

export type TableStepDecision = 'ok' | 'need-existing-table' | 'need-name';

export function tableStepDecision(state: {
  tableMode: 'new' | 'existing';
  existingTableId: string;
  tableName: string;
  mappingName: string;
}): TableStepDecision {
  if (state.tableMode === 'existing') {
    return state.existingTableId ? 'ok' : 'need-existing-table';
  }
  // A blank table name falls back to the mapping name, so either will do.
  return state.tableName.trim() || state.mappingName.trim()
    ? 'ok'
    : 'need-name';
}

export const MAPPING_SLUG_RE = /^[a-z0-9][a-z0-9_-]*$/;

export type MappingFieldProblem = 'missing-name' | 'missing-slug' | 'bad-slug';

/** Field-level problems on the final step, in the order they should be shown. */
export function mappingStepProblems(state: {
  mappingName: string;
  mappingSlug: string;
}): { name?: MappingFieldProblem; slug?: MappingFieldProblem } {
  const problems: { name?: MappingFieldProblem; slug?: MappingFieldProblem } =
    {};
  if (!state.mappingName.trim()) problems.name = 'missing-name';

  const slug = state.mappingSlug.trim();
  if (!slug) problems.slug = 'missing-slug';
  else if (!MAPPING_SLUG_RE.test(slug)) problems.slug = 'bad-slug';

  return problems;
}
